/**
 * @fileoverview Simple SVG Sparkline Chart
 * Lightweight, dependency-free sparklines for signal cards.
 * 
 * Enhanced with:
 * - Adaptive x-axis date labels based on time range
 * - Downsampling for large datasets
 * - Range-aware formatting
 */

const CHART_COLORS = {
  line: '#00EEE5',
  fill: 'rgba(0, 238, 229, 0.15)',
  grid: 'rgba(151, 184, 160, 0.1)',
  text: '#97B8A0',
  textMuted: 'rgba(151, 184, 160, 0.6)',
  eventLine: 'rgba(212, 168, 95, 0.6)',  // Lantern gold for events
  eventMarker: '#D4A85F',
  status: {
    low: '#97B8A0',
    normal: '#00EEE5',
    elevated: '#f97316',
    high: '#B2332A',
    extreme: '#FF0033',
  }
};

/**
 * Time range configurations for adaptive label formatting
 */
const RANGE_CONFIG = {
  '30d': { 
    labelCount: 5, 
    format: 'short',      // M/D
    downsample: 1         // Show all points
  },
  '90d': { 
    labelCount: 4, 
    format: 'short',      // M/D
    downsample: 1 
  },
  '1y': { 
    labelCount: 5, 
    format: 'medium',     // MMM 'YY
    downsample: 2         // Every 2nd point
  },
  '5y': { 
    labelCount: 6, 
    format: 'year',       // YYYY or MMM 'YY
    downsample: 5         // Every 5th point
  },
  'max': { 
    labelCount: 6, 
    format: 'year',
    downsample: 10        // Every 10th point for very long ranges
  },
};

/**
 * Format a timestamp based on range type
 */
function formatDateForRange(timestamp, format) {
  const d = new Date(timestamp * 1000);
  
  switch (format) {
    case 'short':
      return `${d.getMonth() + 1}/${d.getDate()}`;
    case 'medium':
      return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    case 'year':
      return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    case 'yearOnly':
      return d.getFullYear().toString();
    default:
      return `${d.getMonth() + 1}/${d.getDate()}`;
  }
}

/**
 * Parse historical event date strings to timestamps
 * Handles formats like 'Mar 2020', 'Oct 2022', 'Q4 2018'
 */
function parseEventDate(dateStr) {
  if (!dateStr) return null;
  
  // Handle quarter format 'Q1 2020'
  const quarterMatch = dateStr.match(/Q(\d)\s+(\d{4})/);
  if (quarterMatch) {
    const quarter = parseInt(quarterMatch[1]);
    const year = parseInt(quarterMatch[2]);
    // Q1 = Jan, Q2 = Apr, Q3 = Jul, Q4 = Oct
    const month = (quarter - 1) * 3;
    return Math.floor(new Date(year, month, 15).getTime() / 1000);
  }
  
  // Handle 'MMM YYYY' format like 'Mar 2020'
  const monthYearMatch = dateStr.match(/([A-Za-z]+)\s+(\d{4})/);
  if (monthYearMatch) {
    const date = new Date(`${monthYearMatch[1]} 15, ${monthYearMatch[2]}`);
    if (!isNaN(date.getTime())) {
      return Math.floor(date.getTime() / 1000);
    }
  }
  
  // Fallback: try direct parsing
  const parsed = new Date(dateStr);
  if (!isNaN(parsed.getTime())) {
    return Math.floor(parsed.getTime() / 1000);
  }
  
  return null;
}

/**
 * Filter events to those visible within chart time range
 */
function getVisibleEvents(events, chartData) {
  if (!events || !events.length || !chartData || chartData.length < 2) return [];
  
  const minTime = chartData[0].time;
  const maxTime = chartData[chartData.length - 1].time;
  
  return events
    .map(event => ({
      ...event,
      timestamp: parseEventDate(event.date)
    }))
    .filter(event => {
      return event.timestamp && event.timestamp >= minTime && event.timestamp <= maxTime;
    });
}

/**
 * Detect the appropriate range config based on data span
 */
function detectRangeConfig(data) {
  if (!data || data.length < 2) return RANGE_CONFIG['30d'];
  
  const firstTime = data[0].time;
  const lastTime = data[data.length - 1].time;
  const spanDays = (lastTime - firstTime) / (60 * 60 * 24);
  
  if (spanDays <= 45) return RANGE_CONFIG['30d'];
  if (spanDays <= 120) return RANGE_CONFIG['90d'];
  if (spanDays <= 400) return RANGE_CONFIG['1y'];
  if (spanDays <= 2000) return RANGE_CONFIG['5y'];
  return RANGE_CONFIG['max'];
}

/**
 * Downsample data for performance while preserving key points
 * Always keeps first, last, min, and max points
 */
function downsampleData(data, factor) {
  if (factor <= 1 || data.length <= 100) return data;
  
  // Find min and max indices
  let minIdx = 0, maxIdx = 0;
  let minVal = data[0].value, maxVal = data[0].value;
  
  data.forEach((d, i) => {
    if (d.value < minVal) { minVal = d.value; minIdx = i; }
    if (d.value > maxVal) { maxVal = d.value; maxIdx = i; }
  });
  
  // Sample every Nth point, but always include critical points
  const criticalIndices = new Set([0, data.length - 1, minIdx, maxIdx]);
  const sampled = [];
  
  data.forEach((d, i) => {
    if (criticalIndices.has(i) || i % factor === 0) {
      sampled.push(d);
    }
  });
  
  return sampled.sort((a, b) => a.time - b.time);
}

/**
 * Generate evenly spaced x-axis label positions
 */
function generateXAxisLabels(sortedData, labelCount, format, chartWidth, paddingLeft) {
  if (sortedData.length < 2) return [];
  
  const labels = [];
  const dataLength = sortedData.length;
  
  for (let i = 0; i < labelCount; i++) {
    // Calculate position (0 = start, labelCount-1 = end)
    const progress = i / (labelCount - 1);
    const dataIndex = Math.round(progress * (dataLength - 1));
    const x = paddingLeft + progress * chartWidth;
    
    const point = sortedData[dataIndex];
    if (!point) continue;
    
    // Determine text anchor
    let anchor = 'middle';
    if (i === 0) anchor = 'start';
    if (i === labelCount - 1) anchor = 'end';
    
    labels.push({
      x,
      text: formatDateForRange(point.time, format),
      anchor,
    });
  }
  
  return labels;
}

/**
 * Creates an SVG sparkline chart (small, for cards)
 */
export function createSparkline(container, data, options = {}) {
  const { 
    height = 60, 
    status = 'normal',
    showArea = true,
    showDots = false,
    padding = { top: 8, right: 8, bottom: 8, left: 8 }
  } = options;
  
  const lineColor = CHART_COLORS.status[status] || CHART_COLORS.line;
  
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'sparkline-chart');
  svg.style.width = '100%';
  svg.style.height = `${height}px`;
  svg.style.display = 'block';
  
  container.innerHTML = '';
  container.appendChild(svg);
  
  function render(chartData) {
    if (!chartData || chartData.length === 0) {
      svg.innerHTML = `<text x="50%" y="50%" text-anchor="middle" fill="${CHART_COLORS.text}" font-size="11" font-family="'JetBrains Mono', monospace">No data</text>`;
      return;
    }
    
    const sortedData = [...chartData].sort((a, b) => a.time - b.time);
    
    const width = svg.clientWidth || container.clientWidth || 200;
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;
    
    const values = sortedData.map(d => d.value);
    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);
    const range = maxVal - minVal || 1;
    
    const yPadding = range * 0.1;
    const yMin = minVal - yPadding;
    const yMax = maxVal + yPadding;
    const yRange = yMax - yMin;
    
    const points = sortedData.map((d, i) => {
      const x = padding.left + (i / (sortedData.length - 1 || 1)) * chartWidth;
      const y = padding.top + chartHeight - ((d.value - yMin) / yRange) * chartHeight;
      return { x, y, value: d.value, time: d.time };
    });
    
    const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
    
    const areaD = pathD + 
      ` L ${points[points.length - 1].x.toFixed(1)} ${height - padding.bottom}` +
      ` L ${points[0].x.toFixed(1)} ${height - padding.bottom} Z`;
    
    let svgContent = '';
    
    if (showArea) {
      svgContent += `<path d="${areaD}" fill="${lineColor}" fill-opacity="0.15" stroke="none"/>`;
    }
    
    svgContent += `<path d="${pathD}" fill="none" stroke="${lineColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`;
    
    if (points.length > 0) {
      const lastPoint = points[points.length - 1];
      svgContent += `<circle cx="${lastPoint.x.toFixed(1)}" cy="${lastPoint.y.toFixed(1)}" r="3" fill="${lineColor}"/>`;
    }
    
    if (showDots) {
      points.forEach(p => {
        svgContent += `<circle cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="2" fill="${lineColor}" fill-opacity="0.5"/>`;
      });
    }
    
    svg.innerHTML = svgContent;
  }
  
  render(data);
  
  const resizeObserver = new ResizeObserver(() => render(data));
  resizeObserver.observe(container);
  
  return {
    update: (newData) => render(newData),
    destroy: () => {
      resizeObserver.disconnect();
      container.innerHTML = '';
    }
  };
}

/**
 * Creates a larger interactive chart for the expanded modal
 * Enhanced with adaptive x-axis labels, downsampling, and historical event overlays
 */
export function createExpandedChart(container, data, options = {}) {
  const { 
    height = 300, 
    status = 'normal',
    showGrid = true,
    showLabels = true,
    rangeId = null,  // '30d', '90d', '1y', '5y', 'max'
    historicalEvents = [],  // Array of { date, value, event, description }
    showEvents = true,  // Toggle event visibility
    padding = { top: 20, right: 60, bottom: 35, left: 10 }
  } = options;
  
  const lineColor = CHART_COLORS.status[status] || CHART_COLORS.line;
  
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'expanded-chart');
  svg.style.width = '100%';
  svg.style.height = `${height}px`;
  svg.style.display = 'block';
  
  container.innerHTML = '';
  container.appendChild(svg);
  
  // Tooltip element for data points
  const tooltip = document.createElement('div');
  tooltip.className = 'chart-tooltip';
  tooltip.style.cssText = `
    position: absolute;
    background: rgba(14, 19, 23, 0.95);
    border: 1px solid ${lineColor};
    padding: 8px 12px;
    border-radius: 4px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: ${CHART_COLORS.text};
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.15s;
    z-index: 100;
  `;
  container.style.position = 'relative';
  container.appendChild(tooltip);
  
  // Tooltip element for historical events
  const eventTooltip = document.createElement('div');
  eventTooltip.className = 'chart-event-tooltip';
  eventTooltip.style.cssText = `
    position: absolute;
    background: rgba(14, 19, 23, 0.98);
    border: 1px solid ${CHART_COLORS.eventMarker};
    padding: 10px 14px;
    border-radius: 6px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: ${CHART_COLORS.text};
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s;
    z-index: 110;
    max-width: 280px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  `;
  container.appendChild(eventTooltip);
  
  let currentData = data;
  let displayPoints = [];  // Downsampled for display
  let allPoints = [];      // Full data for tooltip lookup
  let visibleEvents = [];  // Events within visible range with x positions
  
  function render(chartData) {
    currentData = chartData;
    
    if (!chartData || chartData.length === 0) {
      svg.innerHTML = `<text x="50%" y="50%" text-anchor="middle" fill="${CHART_COLORS.text}" font-size="12" font-family="'JetBrains Mono', monospace">No data available</text>`;
      return;
    }
    
    const sortedData = [...chartData].sort((a, b) => a.time - b.time);
    
    // Detect or use provided range config
    const rangeConfig = rangeId && RANGE_CONFIG[rangeId] 
      ? RANGE_CONFIG[rangeId] 
      : detectRangeConfig(sortedData);
    
    // Downsample for display
    const displayData = downsampleData(sortedData, rangeConfig.downsample);
    
    const width = svg.clientWidth || container.clientWidth || 600;
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;
    
    // Calculate scales from FULL data (not downsampled)
    const values = sortedData.map(d => d.value);
    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);
    const range = maxVal - minVal || 1;
    const yPadding = range * 0.1;
    const yMin = minVal - yPadding;
    const yMax = maxVal + yPadding;
    const yRange = yMax - yMin;
    
    // Generate points for display (downsampled)
    displayPoints = displayData.map((d, i) => {
      // X position based on time, not index (for proper spacing)
      const timeRange = sortedData[sortedData.length - 1].time - sortedData[0].time;
      const timeOffset = d.time - sortedData[0].time;
      const x = padding.left + (timeOffset / timeRange) * chartWidth;
      const y = padding.top + chartHeight - ((d.value - yMin) / yRange) * chartHeight;
      return { x, y, value: d.value, time: d.time };
    });
    
    // Generate all points for tooltip lookup
    allPoints = sortedData.map((d) => {
      const timeRange = sortedData[sortedData.length - 1].time - sortedData[0].time;
      const timeOffset = d.time - sortedData[0].time;
      const x = padding.left + (timeOffset / timeRange) * chartWidth;
      const y = padding.top + chartHeight - ((d.value - yMin) / yRange) * chartHeight;
      return { x, y, value: d.value, time: d.time };
    });
    
    let svgContent = '';
    
    // Grid lines
    if (showGrid) {
      const gridLines = 5;
      for (let i = 0; i <= gridLines; i++) {
        const y = padding.top + (i / gridLines) * chartHeight;
        svgContent += `<line x1="${padding.left}" y1="${y}" x2="${width - padding.right}" y2="${y}" stroke="${CHART_COLORS.grid}" stroke-dasharray="2,2"/>`;
      }
    }
    
    // Y-axis labels
    if (showLabels) {
      const labelCount = 5;
      for (let i = 0; i <= labelCount; i++) {
        const y = padding.top + (i / labelCount) * chartHeight;
        const value = yMax - (i / labelCount) * yRange;
        
        // Smart formatting based on value magnitude
        let label;
        if (Math.abs(value) >= 1000000) {
          label = (value / 1000000).toFixed(2) + 'M';
        } else if (Math.abs(value) >= 1000) {
          label = (value / 1000).toFixed(1) + 'k';
        } else if (Math.abs(value) < 0.01) {
          label = value.toFixed(4);
        } else {
          label = value.toFixed(2);
        }
        
        svgContent += `<text x="${width - padding.right + 5}" y="${y + 4}" fill="${CHART_COLORS.text}" font-size="10" font-family="'JetBrains Mono', monospace">${label}</text>`;
      }
    }
    
    // X-axis labels (adaptive based on range)
    if (showLabels && sortedData.length > 0) {
      const xLabels = generateXAxisLabels(
        sortedData, 
        rangeConfig.labelCount, 
        rangeConfig.format,
        chartWidth,
        padding.left
      );
      
      xLabels.forEach(label => {
        svgContent += `<text x="${label.x}" y="${height - 8}" fill="${CHART_COLORS.textMuted}" font-size="10" font-family="'JetBrains Mono', monospace" text-anchor="${label.anchor}">${label.text}</text>`;
      });
    }
    
    // Area and line (using display points)
    if (displayPoints.length > 1) {
      const pathD = displayPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
      const areaD = pathD + 
        ` L ${displayPoints[displayPoints.length - 1].x.toFixed(1)} ${padding.top + chartHeight}` +
        ` L ${displayPoints[0].x.toFixed(1)} ${padding.top + chartHeight} Z`;
      
      svgContent += `<path d="${areaD}" fill="${lineColor}" fill-opacity="0.1" stroke="none"/>`;
      svgContent += `<path d="${pathD}" fill="none" stroke="${lineColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`;
    }
    
    // Current value dot (last point)
    if (displayPoints.length > 0) {
      const lastPoint = displayPoints[displayPoints.length - 1];
      svgContent += `<circle cx="${lastPoint.x.toFixed(1)}" cy="${lastPoint.y.toFixed(1)}" r="4" fill="${lineColor}"/>`;
    }
    
    // Invisible hit area for data tooltip hover (only covers main chart area, not marker zone)
    svgContent += `<rect x="${padding.left}" y="${padding.top + 20}" width="${chartWidth}" height="${chartHeight - 20}" fill="transparent" class="chart-hit-area"/>`;
    
    // Historical event overlays - rendered AFTER hit area so markers are on top
    if (showEvents && historicalEvents && historicalEvents.length > 0) {
      const timeMin = sortedData[0].time;
      const timeMax = sortedData[sortedData.length - 1].time;
      const timeRange = timeMax - timeMin;
      
      // Filter and position events
      visibleEvents = getVisibleEvents(historicalEvents, sortedData).map(event => {
        const x = padding.left + ((event.timestamp - timeMin) / timeRange) * chartWidth;
        return { ...event, x };
      });
      
      // Render event lines and markers
      visibleEvents.forEach((event, idx) => {
        const x = event.x.toFixed(1);
        
        // Vertical dashed line (no hover needed)
        svgContent += `
          <line 
            x1="${x}" y1="${padding.top}" 
            x2="${x}" y2="${padding.top + chartHeight}" 
            stroke="${CHART_COLORS.eventLine}" 
            stroke-width="1" 
            stroke-dasharray="4,4"
            class="chart-event-line"
          />`;
        
        // Larger invisible hit area for easier hovering
        svgContent += `
          <circle 
            cx="${x}" cy="${padding.top + 8}" 
            r="14" 
            fill="transparent" 
            class="chart-event-hitarea"
            data-event-idx="${idx}"
            style="cursor: pointer;"
          />`;
        
        // Visible marker
        svgContent += `
          <circle 
            cx="${x}" cy="${padding.top + 8}" 
            r="6" 
            fill="${CHART_COLORS.eventMarker}" 
            stroke="rgba(14, 19, 23, 0.8)" 
            stroke-width="2"
            class="chart-event-marker"
            data-event-idx="${idx}"
            style="cursor: pointer;"
          />`;
        
        // Small diamond icon inside marker (no pointer events)
        svgContent += `
          <text 
            x="${x}" y="${padding.top + 11}" 
            text-anchor="middle" 
            fill="rgba(14, 19, 23, 0.9)" 
            font-size="7" 
            font-weight="bold"
            class="chart-event-icon"
            style="pointer-events: none;"
          >◆</text>`;
      });
    }
    
    svg.innerHTML = svgContent;
  }
  
  // Mouse interaction - use allPoints for accurate tooltip
  svg.addEventListener('mousemove', (e) => {
    if (!allPoints.length) return;
    
    const rect = svg.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    
    // Find closest point
    let closest = allPoints[0];
    let closestDist = Math.abs(mouseX - closest.x);
    
    for (const p of allPoints) {
      const dist = Math.abs(mouseX - p.x);
      if (dist < closestDist) {
        closest = p;
        closestDist = dist;
      }
    }
    
    // Update tooltip
    const date = new Date(closest.time * 1000);
    const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    
    // Smart value formatting
    let valueStr;
    if (Math.abs(closest.value) >= 1000000) {
      valueStr = (closest.value / 1000000).toFixed(2) + 'M';
    } else if (Math.abs(closest.value) >= 1000) {
      valueStr = closest.value.toLocaleString('en-US', { maximumFractionDigits: 2 });
    } else {
      valueStr = closest.value.toFixed(4);
    }
    
    tooltip.innerHTML = `<div style="color: ${lineColor}">${valueStr}</div><div style="opacity: 0.7">${dateStr}</div>`;
    tooltip.style.opacity = '1';
    tooltip.style.left = `${closest.x}px`;
    tooltip.style.top = `${closest.y - 50}px`;
    tooltip.style.transform = 'translateX(-50%)';
  });
  
  svg.addEventListener('mouseleave', () => {
    tooltip.style.opacity = '0';
  });
  
  // Event marker interactions - check for data-event-idx attribute
  svg.addEventListener('mouseover', (e) => {
    const target = e.target;
    const idx = target.getAttribute('data-event-idx');
    if (idx !== null && visibleEvents.length > 0) {
      const event = visibleEvents[parseInt(idx, 10)];
      if (event) {
        showEventTooltip(event);
      }
    }
  });
  
  svg.addEventListener('mouseout', (e) => {
    const target = e.target;
    if (target.getAttribute('data-event-idx') !== null) {
      eventTooltip.style.opacity = '0';
    }
  });
  
  // Touch support for mobile
  svg.addEventListener('touchstart', (e) => {
    const target = e.target;
    const idx = target.getAttribute('data-event-idx');
    if (idx !== null && visibleEvents.length > 0) {
      e.preventDefault();
      const event = visibleEvents[parseInt(idx, 10)];
      if (event) {
        showEventTooltip(event);
        // Auto-hide after 3 seconds on touch
        setTimeout(() => {
          eventTooltip.style.opacity = '0';
        }, 3000);
      }
    }
  }, { passive: false });
  
  function showEventTooltip(event) {
    const eventHtml = `
      <div style="color: ${CHART_COLORS.eventMarker}; font-weight: 600; margin-bottom: 4px;">${event.event}</div>
      <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
        <span style="color: ${CHART_COLORS.textMuted};">${event.date}</span>
        <span style="color: ${lineColor}; font-weight: 500;">${event.value}</span>
      </div>
      <div style="color: ${CHART_COLORS.text}; font-size: 10px; line-height: 1.4;">${event.description}</div>
    `;
    
    eventTooltip.innerHTML = eventHtml;
    eventTooltip.style.opacity = '1';
    
    // Position tooltip above the marker
    const containerRect = container.getBoundingClientRect();
    const tooltipWidth = 250; // Approximate width
    let leftPos = event.x - tooltipWidth / 2;
    
    // Keep tooltip within container bounds
    if (leftPos < 10) leftPos = 10;
    if (leftPos + tooltipWidth > containerRect.width - 10) {
      leftPos = containerRect.width - tooltipWidth - 10;
    }
    
    eventTooltip.style.left = `${leftPos}px`;
    eventTooltip.style.top = `${padding.top + 25}px`;
  }
  
  render(data);
  
  const resizeObserver = new ResizeObserver(() => render(currentData));
  resizeObserver.observe(container);
  
  // Track events visibility state
  let eventsVisible = showEvents;
  
  return {
    update: (newData) => render(newData),
    toggleEvents: (visible) => {
      eventsVisible = visible !== undefined ? visible : !eventsVisible;
      // Show/hide event elements
      const eventLines = svg.querySelectorAll('.chart-event-line');
      const eventMarkers = svg.querySelectorAll('.chart-event-marker');
      const eventIcons = svg.querySelectorAll('.chart-event-icon');
      const eventHitareas = svg.querySelectorAll('.chart-event-hitarea');
      
      const display = eventsVisible ? '' : 'none';
      eventLines.forEach(el => el.style.display = display);
      eventMarkers.forEach(el => el.style.display = display);
      eventIcons.forEach(el => el.style.display = display);
      eventHitareas.forEach(el => el.style.display = display);
      
      if (!eventsVisible) {
        eventTooltip.style.opacity = '0';
      }
      
      return eventsVisible;
    },
    getEventsVisible: () => eventsVisible,
    getVisibleEventsCount: () => visibleEvents.length,
    destroy: () => {
      resizeObserver.disconnect();
      container.innerHTML = '';
    }
  };
}

export { CHART_COLORS, RANGE_CONFIG, downsampleData, parseEventDate, getVisibleEvents };
